import './SAEditCustomerModal.scss';
import React, {useEffect} from "react";
import {FormInput} from "../../Form/Input/FormInput";
import {i18n} from "../../../Utils/i18n/i18n";
import {Button} from "../../Form/Button/Button";
import {buttonColors} from "../../Pickers/InputTheme";
import {SA_getCompanyAndAdminUsers, SA_updateCompany, SA_updateCompanyUsersMfaAPI} from "../../../Services/SA_api";
import {GridReactContext, GridReactContextProvider} from "../../../Utils/DataGridWrapper/GridContext";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import {EditAddOrganizationsToCompany} from "../EditAddOrganizationsToCompany/EditAddOrganizationsToCompany";
import {Modal} from "../../Smart/Modal/Modal";
import {get} from "lodash";
import {FormControlLabel, Switch} from "@mui/material";
import {CountrySelect} from "../../Form/CountrySelect/CountrySelect";
import {DEFAULT_COUNTRY_CODE} from "../../../Utils/Constants";

export const SAEditCustomerModal = ({
                                        onUserEditCompleted,
                                        initCustomerId,
                                        editCustomerModalOpen,
                                        closeEditCustomerModal,
                                    }) => {
    const [companyName, setCompanyName] = React.useState()
    const [companyUsersPrefix, setCompanyUserPrefix] = React.useState()
    const [identifier, setIdentifier] = React.useState()
    const [contactPhone, setContactPhone] = React.useState()
    const [contactEmail, setContactEmail] = React.useState()
    const [errorPrefix, setErrorPrefix] = React.useState(false)
    const [contactFirstName, setContactFirstName] = React.useState()
    const [contactLastName, setContactLastName] = React.useState()
    const [countryCode, setCountryCode] = React.useState(DEFAULT_COUNTRY_CODE)
    const [isMfaEnabled, setIsMfaEnabled] = React.useState(false)
    const initialMfaEnabled = React.useRef(false)
    const [isLoading, setIsLoading] = React.useState(false)
    const gridContext = React.useContext(GridReactContext)

    const init = async () => {
        setIsLoading(true)
        try {
            const companyRes = await SA_getCompanyAndAdminUsers({companyId: initCustomerId})

            if (!companyRes) {
                console.error('SA_getCompanyAndAdminUsers returned null or undefined')
                setIsLoading(false)
                return
            }

            //company
            setCompanyName(get(companyRes, 'companyName', ''))
            setCompanyUserPrefix(get(companyRes, 'companyUsersPrefix', ''))
            setIdentifier(get(companyRes, 'identifier', ''))
            setContactPhone(get(companyRes, 'contactPhone', ''))
            setContactEmail(get(companyRes, 'contactEmail', ''))
            setContactFirstName(get(companyRes, 'contactFirstName', ''))
            setContactLastName(get(companyRes, 'contactLastName', ''))
            setCountryCode(get(companyRes, 'countryCode', ''))
            const mfaValue = get(companyRes, 'isMfaEnabledForAllUsers', false)
            setIsMfaEnabled(mfaValue)
            initialMfaEnabled.current = mfaValue
        } catch (error) {
            console.error('Error loading company data:', error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        init()
    }, []);

    const onEditCompanyName = (e) => {
        setCompanyName(e.target.value)
    }
    const onEditCompanyUserPrefix = (e) => {
        //check if it only contains english letters and numbers
        const trimmed = e.target.value.replace(/[^a-zA-Z0-9]/g, '')
        if (trimmed !== e.target.value) {
            setErrorPrefix(true)
        } else {
            setErrorPrefix(false)
        }
        //capitalize
        setCompanyUserPrefix(e.target.value.toUpperCase())
    }
    const onEditIdentifier = (e) => {
        setIdentifier(e.target.value)
    }
    const onEditContactPhone = (e) => {
        setContactPhone(e.target.value)
    }
    const onEditContactEmail = (e) => {
        setContactEmail(e.target.value)
    }

    const onEditContactFirstName = (e) => {
        setContactFirstName(e.target.value)
    }
    const onEditContactLastName = (e) => {
        setContactLastName(e.target.value)
    }
    const onCreateCompany = async () => {
        setIsLoading(true)
        const promises = [
            SA_updateCompany({
                companyId: initCustomerId,
                companyName,
                identifier,
                countryCode,
                contactPhone,
                contactEmail,
                contactFirstName,
                contactLastName
            })
        ]
        if (isMfaEnabled !== initialMfaEnabled.current) {
            promises.push(SA_updateCompanyUsersMfaAPI({
                companyId: initCustomerId,
                isMfaRequired: isMfaEnabled
            }))
        }
        await Promise.all(promises)
        setIsLoading(false)
        onUserEditCompleted && onUserEditCompleted()
        gridContext.triggerTableRefresh()
    }

    const buttonDisabled = !companyName || !companyUsersPrefix || !identifier || !countryCode || errorPrefix
    return <Modal
        open={editCustomerModalOpen}
        onClose={closeEditCustomerModal}>
        <div className="SuperAdminEditCustomer modalWrapper">
            <div className="modalTitle">
                <div>{i18n.translate('superAdmin.editCustomer.modalTitle')}</div>
            </div>
            <div className="modalContent">
                <div className="editCustomerContent">
                    <div className="detailsTitle">
                        <div>{i18n.translate('superAdmin.editCustomer.detailsTitle')}</div>
                    </div>
                    {isLoading ? <LoadingSpinner/> : ""}
                    <div className="details">
                        <div className="detailsRow">
                            <FormInput
                                required
                                id={`companyName`}
                                label={i18n.translate('superAdmin.createNewCustomer.companyName')}
                                className=""
                                value={companyName}
                                onChange={onEditCompanyName}
                            />
                            <FormInput
                                required
                                disabled
                                id={`companyUsersPrefix`}
                                label={i18n.translate('superAdmin.createNewCustomer.userPrefix')}
                                error={errorPrefix && i18n.translate('superAdmin.createNewCustomer.userPrefixError')}
                                className=""
                                value={companyUsersPrefix}
                                onChange={onEditCompanyUserPrefix}
                            />
                            <FormInput
                                required
                                id={`identifier`}
                                label={i18n.translate('superAdmin.createNewCustomer.identifier')}
                                className=""
                                value={identifier}
                                onChange={onEditIdentifier}
                            />
                            <CountrySelect
                                label={i18n.translate('superAdmin.createNewCustomer.countryCode')}
                                value={countryCode}
                                onChange={setCountryCode}
                                required
                                dataTestId="countryCode"
                            />
                        </div>


                        <div className="detailsRow">
                            <FormInput
                                id={`contactPhone`}
                                label={i18n.translate('superAdmin.createNewCustomer.contactPhone')}
                                className=""
                                value={contactPhone}
                                onChange={onEditContactPhone}
                            />
                            <FormInput
                                id={`contactEmail`}
                                label={i18n.translate('superAdmin.createNewCustomer.contactEmail')}
                                className=""
                                value={contactEmail}
                                onChange={onEditContactEmail}
                            />
                            <FormInput
                                id={`contactFirstName`}
                                label={i18n.translate('superAdmin.createNewCustomer.contactFirstName')}
                                className=""
                                value={contactFirstName}
                                onChange={onEditContactFirstName}/>
                            <FormInput
                                id={`contactLastName`}
                                label={i18n.translate('superAdmin.createNewCustomer.contactLastName')}
                                className=""
                                value={contactLastName}
                                onChange={onEditContactLastName}/>
                        </div>

                        <div className="detailsRow">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isMfaEnabled}
                                        onChange={(e) => setIsMfaEnabled(e.target.checked)}
                                    />
                                }
                                label={i18n.translate('superAdmin.editCustomer.mfaForAllUsers')}
                            />
                        </div>

                    </div>

                    <div className="organizationsWrapper">
                        <GridReactContextProvider>
                            <EditAddOrganizationsToCompany companyId={initCustomerId}/>
                        </GridReactContextProvider>
                    </div>
                </div>
            </div>

            <div className="modalFooter">
                <Button
                    color={buttonColors.YELLOW}
                    onClick={closeEditCustomerModal}
                    text={i18n.translate('general.close')}
                />
                <Button
                    disabled={buttonDisabled}
                    color={buttonColors.GREY}
                    onClick={onCreateCompany}
                    text={i18n.translate('general.saveChanges')}
                />
            </div>
        </div>
    </Modal>
}